import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import Button from './Button';
import { isChunkLoadError, reloadOnceForChunkError } from '../utils/chunkReload';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Top-level safety net around the whole app.
 *
 * React Router's ``errorElement`` covers route errors, but a crash that happens
 * *outside* the router (a provider's render, an effect in AppShell's parent)
 * would otherwise unmount everything and leave a blank page. This boundary keeps
 * a friendly, actionable screen in front of the user either way, and auto-recovers
 * from a stale-deploy chunk mismatch by reloading once.
 */
export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      reloadOnceForChunkError();
    }
    // Keep a breadcrumb for debugging without crashing the console pipeline.
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const chunk = isChunkLoadError(error);
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-slate-950 px-6 text-center text-slate-100">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/15 text-amber-400">
          <AlertTriangle size={28} aria-hidden="true" />
        </div>
        <div className="max-w-md space-y-2">
          <h1 className="text-xl font-semibold">
            {chunk ? 'Updating the app…' : 'Something went wrong'}
          </h1>
          <p className="text-sm text-slate-400">
            {chunk
              ? 'The app was updated while this page was open. Reload to continue.'
              : 'An unexpected problem stopped the app from loading. Please reload to try again.'}
          </p>
        </div>
        <Button variant="primary" onClick={() => window.location.reload()} data-agent-id="app.boundary.reload">
          <RefreshCw size={16} aria-hidden="true" /> Reload
        </Button>
      </div>
    );
  }
}
